/**
 * TrustPanel — přehled a základní správa trust vztahů uzlů.
 *
 * Kontrakt: MOZEK/acm-trust-contract-canonical-2026-08-14.md
 * - Vrstva A PKI vs Vrstva B klasifikace
 * - Právo tool callu = entita volajícího (jen vysvětleno, ne spravováno tady)
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  GitBranch,
  Link2,
  RefreshCw,
  Shield,
  ShieldCheck,
  ShieldOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../lib/api';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Select,
  StatCard,
  StatsGrid,
} from '../../../components/ui';

type Relation =
  | 'self'
  | 'cluster'
  | 'remote_one_way'
  | 'remote_two_way'
  | 'foreign_untrusted'
  | 'legacy_alias';

interface TrustNode {
  entity_id: string;
  external_id: string;
  stable_id: string;
  label: string;
  route_name: string | null;
  host: string | null;
  enabled: boolean;
  role: string | null;
  trust_mode: string | null;
  relation: Relation;
  relation_label: string;
  control_direction: string;
  capabilities: {
    hub_controls_remote: boolean;
    remote_controls_hub: boolean;
    hub_may_pull_data: boolean;
    platform_services: boolean;
    emqx_peer_bridge: boolean;
    mqtt_spoke_bridge: boolean;
    catalog_db_sync: boolean;
  };
  pki_ownership: string;
  remote_kind: 'ours_remote' | 'foreign_remote' | null;
  remote_kind_label: string | null;
  root_ca_fingerprint: string | null;
  has_remote_ca_pin: boolean;
  edition: string | null;
  last_seen: string | null;
  tenant: { id: string; slug: string; name: string } | null;
  tenant_slug_meta: string | null;
  source: string | null;
}

interface TrustOverview {
  ok: boolean;
  principles: Record<string, string>;
  self: { stable_id: string; hostname: string; label: string };
  summary: {
    total: number;
    cluster: number;
    remote_one_way: number;
    remote_two_way: number;
    legacy_alias: number;
    foreign_untrusted: number;
    remote_ca_pins: number;
    warnings: number;
  };
  ca_hierarchy: {
    root: string | null;
    intermediate: string | null;
    peer_node: string | null;
    emqx_server: string | null;
  };
  remote_ca_pins: Array<{ stable_id: string; subject: string | null }>;
  nodes: TrustNode[];
  warnings: string[];
}

const RELATION_OPTIONS: { value: Relation; labelKey: string; label: string }[] = [
  { value: 'cluster', labelKey: 'mesh.trust.relCluster', label: 'Cluster (full-mesh)' },
  { value: 'remote_one_way', labelKey: 'mesh.trust.relRemoteOneWay', label: 'Remote jednosměrný' },
  { value: 'remote_two_way', labelKey: 'mesh.trust.relRemoteTwoWay', label: 'Remote obousměrný' },
  { value: 'foreign_untrusted', labelKey: 'mesh.trust.relUntrusted', label: 'Nedůvěryhodný' },
  { value: 'legacy_alias', labelKey: 'mesh.trust.relLegacy', label: 'Legacy alias' },
];

function relationBadgeVariant(
  r: Relation,
): 'success' | 'info' | 'warning' | 'error' | 'default' {
  switch (r) {
    case 'cluster':
      return 'success';
    case 'remote_one_way':
      return 'info';
    case 'remote_two_way':
      return 'warning';
    case 'legacy_alias':
      return 'error';
    default:
      return 'default';
  }
}

function CapDot({ on, label }: { on: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded ${
        on ? 'bg-emerald-500/15 text-emerald-300' : 'bg-white/5 text-muted-foreground/50'
      }`}
      title={label}
    >
      {on ? '●' : '○'} {label}
    </span>
  );
}

export function TrustPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<'all' | Relation>('all');

  const q = useQuery({
    queryKey: ['mesh-trust-overview'],
    queryFn: () => api.mcp<TrustOverview>('mesh.trust_overview', {}),
    refetchInterval: 30_000,
  });

  const bootQ = useQuery({
    queryKey: ['mesh-boot-check'],
    queryFn: () =>
      api.mcp<{
        ok: boolean;
        primary_domain: string | null;
        beacon: { status: string; hostname?: string; message?: string } | null;
        offer_create_beacon: { beacon_hostname: string } | null;
        pending_discovery: number;
        actions: string[];
      }>('mesh.boot_check', {}),
    staleTime: 60_000,
  });

  const setMut = useMutation({
    mutationFn: (p: { stable_id: string; relation: Relation; enabled?: boolean }) =>
      api.mcp<{ ok: boolean; error?: string; note?: string }>('mesh.trust_set', p),
    onSuccess: (res) => {
      if (!res.ok) {
        toast.error(res.error ?? 'trust_set selhal');
        return;
      }
      toast.success(t('mesh.trust.saved', 'Vztah uložen'));
      if (res.note) toast.message(res.note);
      void qc.invalidateQueries({ queryKey: ['mesh-trust-overview'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const data = q.data;
  const nodes = useMemo(() => {
    const list = data?.nodes ?? [];
    if (filter === 'all') return list.filter((n) => n.relation !== 'self');
    return list.filter((n) => n.relation === filter);
  }, [data?.nodes, filter]);

  if (q.isLoading) {
    return (
      <div className='py-12 text-center text-sm text-muted-foreground'>
        {t('common.loading', 'Načítám…')}
      </div>
    );
  }

  if (q.isError || !data?.ok) {
    return (
      <div className='text-sm text-red-400 py-8 text-center'>
        {t('mesh.trust.loadFailed', 'mesh.trust_overview se nepodařilo načíst.')}
      </div>
    );
  }

  const s = data.summary;

  return (
    <div className='space-y-4 p-1'>
      {/* Principles strip */}
      <Card className='border-white/10 bg-white/[0.02]'>
        <CardContent className='py-3 px-4 text-xs text-muted-foreground space-y-1'>
          <div className='flex items-start gap-2'>
            <Shield className='w-4 h-4 mt-0.5 shrink-0 text-sky-400' />
            <div>
              <strong className='text-foreground/90'>
                {t('mesh.trust.principleTitle', 'Dvě vrstvy + právo volajícího')}
              </strong>
              <p className='mt-1 leading-relaxed'>
                {t(
                  'mesh.trust.principleBody',
                  'A) PKI — můžou si podat ruku (ours Sub-CA / foreign pin). B) Klasifikace uzlu — cluster full-mesh, remote jednosměrný (hub ovládá, tahá data; platform služby dle entity), remote obousměrný. Oprávnění toolu vždy určuje entita, která volá — ne „jsme kamarádi v mesh“.',
                )}
              </p>
            </div>
          </div>
          <div className='text-[11px] opacity-80 pl-6'>
            {t('mesh.trust.self', 'Tento uzel')}:{' '}
            <code className='text-foreground/80'>{data.self.label}</code>
            {' · '}
            <code className='text-foreground/70'>{data.self.stable_id}</code>
            {' · '}
            <code className='text-foreground/50'>{data.self.hostname}</code>
          </div>
        </CardContent>
      </Card>

      {bootQ.data?.ok && (
        <Card className='border-white/10'>
          <CardContent className='py-3 px-4 text-xs space-y-1'>
            <div className='font-medium text-foreground/90 text-sm'>
              Mesh start check
            </div>
            <div className='text-muted-foreground'>
              domain:{' '}
              <code>{bootQ.data.primary_domain ?? '—'}</code>
              {' · '}
              beacon:{' '}
              <code>
                {bootQ.data.beacon?.status ?? 'n/a'}
                {bootQ.data.beacon?.hostname
                  ? ` (${bootQ.data.beacon.hostname})`
                  : ''}
              </code>
              {bootQ.data.offer_create_beacon && (
                <span className='text-amber-300/90'>
                  {' '}
                  {t('mesh.nabidkaMajaku', '· nabídka majáku:')} {bootQ.data.offer_create_beacon.beacon_hostname}{' '}
                  {t('mesh.pozdeji', '(později)')}
                </span>
              )}
              {bootQ.data.pending_discovery > 0 && (
                <span className='text-sky-300'>
                  {' '}
                  · pending discovery: {bootQ.data.pending_discovery}
                </span>
              )}
            </div>
            {bootQ.data.actions?.[0] && (
              <div className='text-[11px] text-muted-foreground/80'>
                {bootQ.data.actions[0]}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className='flex items-center justify-between gap-2 flex-wrap'>
        <StatsGrid className='flex-1 min-w-0'>
          <StatCard label='Cluster' value={String(s.cluster)} />
          <StatCard label='Remote 1→' value={String(s.remote_one_way)} />
          <StatCard label='Remote ↔' value={String(s.remote_two_way)} />
          <StatCard label='Legacy' value={String(s.legacy_alias)} />
          <StatCard label='CA pins' value={String(s.remote_ca_pins)} />
        </StatsGrid>
        <Button
          variant='secondary'
          size='sm'
          onClick={() => void q.refetch()}
          disabled={q.isFetching}
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${q.isFetching ? 'animate-spin' : ''}`} />
          {t('common.refresh', 'Obnovit')}
        </Button>
      </div>

      {data.warnings.length > 0 && (
        <Card className='border-amber-500/30 bg-amber-500/5'>
          <CardHeader className='py-2 px-4'>
            <CardTitle className='text-sm flex items-center gap-2 text-amber-300'>
              <AlertTriangle className='w-4 h-4' />
              {t('mesh.trust.warnings', 'Varování')} ({data.warnings.length})
            </CardTitle>
          </CardHeader>
          <CardContent className='px-4 pb-3 space-y-1'>
            {data.warnings.map((w, i) => (
              <div key={i} className='text-xs text-amber-100/90'>
                {w}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className='grid md:grid-cols-2 gap-3'>
        {/* CA hierarchy */}
        <Card className='border-white/10'>
          <CardHeader className='py-2 px-4'>
            <CardTitle className='text-sm flex items-center gap-2'>
              <GitBranch className='w-4 h-4 text-violet-400' />
              {t('mesh.trust.caLocal', 'Lokální CA hierarchie')}
            </CardTitle>
          </CardHeader>
          <CardContent className='px-4 pb-3 text-xs font-mono space-y-2'>
            <CaRow level='Root / chain' subject={data.ca_hierarchy.root} />
            <CaRow level='Intermediate' subject={data.ca_hierarchy.intermediate} />
            <CaRow level='Node leaf' subject={data.ca_hierarchy.peer_node} />
            <CaRow level='EMQX server' subject={data.ca_hierarchy.emqx_server} />
          </CardContent>
        </Card>

        {/* Remote CA pins */}
        <Card className='border-white/10'>
          <CardHeader className='py-2 px-4'>
            <CardTitle className='text-sm flex items-center gap-2'>
              <Link2 className='w-4 h-4 text-sky-400' />
              {t('mesh.trust.remotePins', 'Remote CA pins (hub→remote MCP)')}
            </CardTitle>
          </CardHeader>
          <CardContent className='px-4 pb-3 space-y-1.5 max-h-48 overflow-y-auto'>
            {data.remote_ca_pins.length === 0 ? (
              <EmptyState
                title={t('mesh.trust.noPins', 'Žádné remote CA piny')}
                description={t(
                  'mesh.trust.noPinsHint',
                  'Po spoke enroll by měly přibýt soubory v remote-peer-cas/',
                )}
              />
            ) : (
              data.remote_ca_pins.map((p) => (
                <div
                  key={p.stable_id}
                  className='text-xs border border-white/5 rounded px-2 py-1.5 bg-white/[0.02]'
                >
                  <div className='font-mono text-foreground/90'>{p.stable_id}</div>
                  <div className='text-muted-foreground truncate' title={p.subject ?? ''}>
                    {p.subject ?? '—'}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filter + nodes */}
      <div className='flex items-center gap-2 flex-wrap'>
        <span className='text-xs text-muted-foreground'>
          {t('mesh.trust.nodes', 'Uzly')}
        </span>
        <Select
          value={filter}
          onChange={(e) => setFilter(e.target.value as 'all' | Relation)}
          className='h-8 text-xs w-auto min-w-[10rem]'
        >
          <option value='all'>{t('common.all', 'Vše')}</option>
          {RELATION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {t(o.labelKey, o.label)}
            </option>
          ))}
        </Select>
      </div>

      {nodes.length === 0 ? (
        <EmptyState
          title={t('mesh.trust.empty', 'Žádné uzly v tomto filtru')}
          description=''
        />
      ) : (
        <div className='space-y-2'>
          {nodes.map((n) => (
            <Card
              key={n.entity_id}
              className={`border-white/10 ${!n.enabled ? 'opacity-60' : ''}`}
            >
              <CardContent className='py-3 px-4 space-y-2'>
                <div className='flex flex-wrap items-start justify-between gap-2'>
                  <div className='min-w-0'>
                    <div className='flex items-center gap-2 flex-wrap'>
                      <span className='font-medium text-sm'>{n.label}</span>
                      <Badge variant={relationBadgeVariant(n.relation)}>
                        {n.relation_label}
                      </Badge>
                      {n.remote_kind && (
                        <Badge variant={n.remote_kind === 'ours_remote' ? 'info' : 'warning'}>
                          {n.remote_kind_label ?? n.remote_kind}
                        </Badge>
                      )}
                      {!n.enabled && <Badge variant='gray'>disabled</Badge>}
                      {n.has_remote_ca_pin
                        ? (
                          <span className='inline-flex items-center gap-0.5 text-[11px] text-emerald-400'>
                            <ShieldCheck className='w-3 h-3' /> CA pin
                          </span>
                        )
                        : (n.relation === 'remote_one_way' ||
                            n.relation === 'remote_two_way') && (
                          <span className='inline-flex items-center gap-0.5 text-[11px] text-amber-400'>
                            <ShieldOff className='w-3 h-3' /> {t('mesh.bezPinu', 'bez pinu')}
                          </span>
                        )}
                    </div>
                    <div className='text-[11px] font-mono text-muted-foreground mt-0.5 break-all'>
                      stable_id={n.stable_id}
                      {n.route_name ? ` · route=${n.route_name}` : ''}
                      {n.host ? ` · ${n.host}` : ''}
                    </div>
                    <div className='text-[11px] text-muted-foreground mt-0.5'>
                      {n.tenant
                        ? (
                          <>
                            {t('mesh.trust.tenant', 'Tenant')}:{' '}
                            <strong className='text-foreground/80'>{n.tenant.name}</strong>
                            {' '}({n.tenant.slug})
                          </>
                        )
                        : n.tenant_slug_meta
                        ? (
                          <>
                            {t('mesh.trust.tenantMeta', 'tenant_slug')}: {n.tenant_slug_meta}
                          </>
                        )
                        : (
                          <span className='opacity-60'>
                            {t('mesh.trust.noTenant', 'bez tenanta')}
                          </span>
                        )}
                      {n.edition ? ` · edition=${n.edition}` : ''}
                      {n.pki_ownership ? ` · pki=${n.pki_ownership}` : ''}
                      {n.root_ca_fingerprint
                        ? ` · root=${n.root_ca_fingerprint.slice(0, 20)}…`
                        : ''}
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Select
                      value={n.relation}
                      disabled={setMut.isPending || n.relation === 'self'}
                      className='h-8 text-xs w-[11rem]'
                      onChange={(e) => {
                        const relation = e.target.value as Relation;
                        if (relation === n.relation) return;
                        setMut.mutate({
                          stable_id: n.stable_id || n.external_id,
                          relation,
                        });
                      }}
                    >
                      {RELATION_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {t(o.labelKey, o.label)}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
                <div className='flex flex-wrap gap-1'>
                  <CapDot on={n.capabilities.hub_controls_remote} label='hub→node' />
                  <CapDot on={n.capabilities.remote_controls_hub} label='node→hub' />
                  <CapDot on={n.capabilities.hub_may_pull_data} label='pull data' />
                  <CapDot on={n.capabilities.platform_services} label='platform/SSO' />
                  <CapDot on={n.capabilities.emqx_peer_bridge} label='EMQX mesh' />
                  <CapDot on={n.capabilities.mqtt_spoke_bridge} label='spoke MQTT' />
                  <CapDot on={n.capabilities.catalog_db_sync} label='catalog DB' />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CaRow({ level, subject }: { level: string; subject: string | null }) {
  return (
    <div className='flex gap-2 items-start'>
      <span className='text-muted-foreground w-28 shrink-0'>{level}</span>
      <span className='text-foreground/80 break-all'>{subject ?? '—'}</span>
    </div>
  );
}

export default TrustPanel;
